import { useEffect, useMemo, useState } from 'react';

import { useImportMappings } from '../../../hooks/useImportMappings';
import { useTogglDataContext } from '../../../screens/TogglDataProvider';
import { TogglWorkspace } from '../../../types/toggl-api';
import { Wrapper } from '../wrapper';
import { Filters, SearchFilters } from './components/SearchFilters';
import { TimeEntriesTable } from './components/TimeEntriesTable';
import { useTimeEntries } from './hooks/useTimeEntries';
import { filterTimeEntries, getThirtyDaysAgo, getToday } from './utils';

function getDefaultFiltersState(workspaces: TogglWorkspace[]) {
  return {
    workspaceIds: workspaces.map((w) => w.id),
    search: '',
    startDate: getThirtyDaysAgo(),
    endDate: getToday(),
  };
}

export function ConfigMyEntries() {
  const { workspaces, id: togglUserId } = useTogglDataContext();
  const [filters, setFilters] = useState<Filters>(
    getDefaultFiltersState(workspaces)
  );

  const { isLoading: isLoadingImportMappings, fetchNewMappings } =
    useImportMappings();

  const selectedWorkspacesIds = filters.workspaceIds.length
    ? filters.workspaceIds
    : workspaces.map((w) => w.id);

  const { isLoading: isLoadingTimeEntries, timeEntries } = useTimeEntries(
    selectedWorkspacesIds,
    togglUserId,
    filters.startDate,
    filters.endDate
  );

  const [selectedTimeEntryIds, setSelectedTimeEntryIds] = useState<number[]>(
    []
  );

  useEffect(() => {
    setSelectedTimeEntryIds((prev) =>
      prev.filter((id) => timeEntries.find((timeEntry) => timeEntry.id === id))
    );
  }, [timeEntries]);

  const filteredTimeEntries = useMemo(
    () => filterTimeEntries(timeEntries, filters),
    [timeEntries, filters]
  );

  useEffect(() => {
    setSelectedTimeEntryIds((prev) =>
      prev.filter((id) =>
        filteredTimeEntries.find((timeEntry) => timeEntry.id === id)
      )
    );
  }, [filteredTimeEntries]);

  useEffect(() => {
    if (!isLoadingTimeEntries) {
      void fetchNewMappings(
        filteredTimeEntries.map((entry) => String(entry.id))
      );
    }
  }, [isLoadingTimeEntries, filteredTimeEntries]);

  return (
    <Wrapper>
      <SearchFilters
        filters={filters}
        setFilters={setFilters}
        workspaces={workspaces}
      />
      <TimeEntriesTable
        isLoading={isLoadingTimeEntries || isLoadingImportMappings}
        timeEntries={filteredTimeEntries}
        selected={selectedTimeEntryIds}
        setSelected={setSelectedTimeEntryIds}
      />
    </Wrapper>
  );
}
